import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SUPPORTED_LOCALES, LOCALE_META } from '../constants';
import { normalizeLocale } from '../fallback';

const Wrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.55rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: var(--primary-color);
  }
`;

const Flag = styled.span`
  font-size: 0.95rem;
  line-height: 1;
`;

const Code = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const Chevron = styled.span`
  font-size: 0.55rem;
  opacity: 0.65;
  margin-inline-start: -0.05rem;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  inset-inline-end: 0;
  min-width: 188px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  z-index: 200;
  overflow: hidden;
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: start;
  padding: 0.65rem 0.85rem;
  border: none;
  background: ${(p) => (p.$active ? 'var(--bg-tertiary)' : 'transparent')};
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background: var(--bg-tertiary);
  }
`;

const OptionFlag = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const OptionCode = styled.span`
  min-width: 1.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const OptionLabel = styled.span`
  flex: 1;
`;

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = normalizeLocale(i18n.language || 'en');
  const meta = LOCALE_META[current] || LOCALE_META.en;

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('language.switchLabel')}
        aria-expanded={open}
      >
        <Flag aria-hidden>{meta.flag}</Flag>
        <Code>{meta.shortCode}</Code>
        <Chevron aria-hidden>▾</Chevron>
        {!compact && <OptionLabel>{t(meta.labelKey)}</OptionLabel>}
      </Trigger>
      {open && (
        <Menu role="menu">
          {SUPPORTED_LOCALES.map((code) => {
            const localeMeta = LOCALE_META[code];
            return (
              <Option
                key={code}
                type="button"
                role="menuitem"
                $active={code === current}
                onClick={() => {
                  i18n.changeLanguage(code);
                  setOpen(false);
                }}
              >
                <OptionFlag aria-hidden>{localeMeta.flag}</OptionFlag>
                <OptionCode>{localeMeta.shortCode}</OptionCode>
                <OptionLabel>{t(localeMeta.labelKey)}</OptionLabel>
              </Option>
            );
          })}
        </Menu>
      )}
    </Wrap>
  );
}
